import { Injectable } from '@angular/core';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';

/**
 * In-process replacement for supabase-js's default `navigator.locks` lock.
 *
 * zone.js (Angular's change-detection engine) does not patch the Web Locks API, so any
 * async work that runs through `navigator.locks.request` escapes Angular's NgZone. When a
 * Supabase auth call (e.g. `auth.setSession`) takes that path, the continuation — and every
 * data load chained after it — resolves OUTSIDE the zone, so change detection never fires
 * and the view stays empty until an unrelated event (a click, a poll tick) or a full reload
 * triggers it. That's the intermittent "page loads empty, reload fixes it" symptom.
 *
 * This client manages tokens manually (persistSession/autoRefreshToken are off) and already
 * serialises session setup, so cross-tab locking buys us nothing. Serialising the auth
 * operations with plain (zone-patched) promises keeps every Supabase call inside the zone.
 */
const inProcessLock: <R>(name: string, acquireTimeout: number, fn: () => Promise<R>) => Promise<R> = (() => {
  let chain: Promise<unknown> = Promise.resolve();
  return <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
    const run = chain.then(fn, fn);
    chain = run.then(() => undefined, () => undefined);
    return run;
  };
})();

@Injectable({
  providedIn: 'root'
})
export class SupabaseService {
  private supabase: SupabaseClient;
  private currentAccessToken: string | null = null;
  private lastSessionSetAt = 0;
  private ensuringSession: Promise<void> | null = null;
  private readonly SESSION_REFRESH_THRESHOLD_MS = 55 * 60 * 1000; // 55 minutes

  constructor(private authService: AuthenticationService) {
    this.supabase = createClient(environment.apiUrl, environment.apiKey, {
      auth: {
        persistSession: false, // Disable Supabase's session management
        autoRefreshToken: false, // We handle tokens manually
        lock: inProcessLock, // Keep auth calls inside Angular's NgZone (see comment above)
      }
    });
  }

  async getClient(): Promise<SupabaseClient> {
    // Ensure the client has the current session before returning it
    await this.ensureAuthenticated();
    return this.supabase;
  }

  private async ensureAuthenticated(): Promise<void> {
    if (this.ensuringSession) {
      await this.ensuringSession;
      return;
    }

    this.ensuringSession = this.ensureAuthenticatedInternal();
    try {
      await this.ensuringSession;
    } finally {
      this.ensuringSession = null;
    }
  }

  private async ensureAuthenticatedInternal(): Promise<void> {
    const hasValidSession = await this.authService.ensureValidSession();
    if (!hasValidSession) {
      this.currentAccessToken = null;
      this.lastSessionSetAt = 0;
      throw new Error('No active session');
    }

    const userData: any = this.authService.getUserDetail();
    if (userData?.access_token && userData?.user) {
      const shouldRefreshSession =
        this.currentAccessToken !== userData.access_token ||
        Date.now() - this.lastSessionSetAt > this.SESSION_REFRESH_THRESHOLD_MS;

      if (!shouldRefreshSession) {
        return;
      }

      try {
        // Set the session in the Supabase client
        await this.supabase.auth.setSession({
          access_token: userData.access_token,
          refresh_token: userData.refresh_token || 'dummy_refresh_token'
        });
        this.currentAccessToken = userData.access_token;
        this.lastSessionSetAt = Date.now();
      } catch (error) {
        console.warn('Failed to set Supabase session:', error);
        throw error;
      }
    } else {
      this.currentAccessToken = null;
      this.lastSessionSetAt = 0;
      throw new Error('No active session');
    }
  }

  async refreshSession() {
    const hasValidSession = await this.authService.ensureValidSession();
    if (!hasValidSession) {
      throw new Error('No active session');
    }

    await this.ensureAuthenticated();

    const userData: any = this.authService.getUserDetail();
    if (!userData?.access_token || !userData?.user) {
      throw new Error('No active session');
    }

    // Create a session-like object compatible with Supabase
    const session = {
      access_token: userData.access_token,
      user: userData.user,
      expires_at: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
      token_type: 'bearer'
    };
    
    return session;
  }
}